import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import {
  Box,
  ButtonBase,
  Divider,
  Menu,
  MenuItem as MuiMenuItem,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import LogoutConfirmDialog from "../common/LogoutConfirmDialog";
import { useLogoutConfirmation } from "../../../hooks/useLogoutConfirmation";
import { useAuthStore } from "../../../store/useAuthStore";

import { navTokens } from "./navTokens";

const UserActions: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { t: myTranslate } = useTranslation();
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const {
    isLogoutDialogOpen,
    requestLogout,
    closeLogoutDialog,
    confirmLogout,
  } = useLogoutConfirmation();

  // Close menu if user state changes (e.g., after login/logout)
  React.useEffect(() => {
    setAnchorElUser(null);
  }, [user]);

  React.useEffect(() => {
    // Close menu if anchor element is removed from DOM
    if (anchorElUser && !anchorElUser.isConnected) {
      setAnchorElUser(null);
    }
  }, [anchorElUser]);

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  return (
    <>
      {/* Profile/Login/Register */}
      {user ? (
        <ButtonBase
          onClick={handleOpenUserMenu}
          disableRipple
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.5,
            minWidth: 56,
            px: 0.75,
            py: 0.25,
            borderRadius: "10px",
            transition: "background-color 0.2s ease",
            "&:hover": { backgroundColor: navTokens.bgMuted },
            "&:hover .nav-action-icon": {
              color: navTokens.primary,
              borderColor: navTokens.primary,
            },
          }}
        >
          <Box
            className="nav-action-icon"
            sx={{
              width: 34,
              height: 34,
              borderRadius: "10px",
              border: `1px solid ${navTokens.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: navTokens.textMuted,
              transition: "all 0.2s ease",
            }}
          >
            <PersonOutlineRoundedIcon sx={{ fontSize: 20 }} />
          </Box>
          <Typography
            variant="caption"
            className="notranslate"
            translate="no"
            sx={{
              color: navTokens.textMuted,
              fontSize: "11px",
              lineHeight: 1,
              fontWeight: 600,
              textAlign: "center",
            }}
          >
            {myTranslate("navbar.profile")}
          </Typography>
        </ButtonBase>
      ) : (
        <ButtonBase
          onClick={() => navigate("/login")}
          disableRipple
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.5,
            minWidth: 56,
            px: 0.75,
            py: 0.25,
            borderRadius: "10px",
            transition: "background-color 0.2s ease",
            "&:hover": { backgroundColor: navTokens.bgMuted },
            "&:hover .nav-action-icon": {
              color: navTokens.primary,
              borderColor: navTokens.primary,
            },
          }}
        >
          <Box
            className="nav-action-icon"
            sx={{
              width: 34,
              height: 34,
              borderRadius: "10px",
              border: `1px solid ${navTokens.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: navTokens.textMuted,
              transition: "all 0.2s ease",
            }}
          >
            <PersonOutlineRoundedIcon sx={{ fontSize: 20 }} />
          </Box>
          <Typography
            variant="caption"
            className="notranslate"
            translate="no"
            sx={{
              color: navTokens.textMuted,
              fontSize: "11px",
              lineHeight: 1,
              fontWeight: 600,
              textAlign: "center",
            }}
          >
            {myTranslate("navbar.profile")}
          </Typography>
        </ButtonBase>
      )}

      {/* Menu - Only rendered when user exists AND anchor is set */}
      {user && anchorElUser && (
        <Menu
          id="menu-appbar"
          anchorEl={anchorElUser}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          open={true}
          onClose={handleCloseUserMenu}
          slotProps={{
            paper: {
              sx: {
                mt: "0.75em",
                minWidth: 140,
                maxWidth: 180,
                borderRadius: "12px",
                border: `1px solid ${navTokens.border}`,
                boxShadow: navTokens.shadow,
                "& .MuiMenuItem-root": {
                  fontSize: "0.875rem",
                  px: 2,
                  py: 1,
                },
              },
            },
          }}
        >
          <MuiMenuItem
            onClick={() => {
              handleCloseUserMenu();
              navigate("/profile");
            }}
          >
            <span className="notranslate" translate="no">
              {myTranslate("navbar.profile")}
            </span>
          </MuiMenuItem>
          {/* <MuiMenuItem
            onClick={() => {
              handleCloseUserMenu();
              navigate("/preferences");
            }}
          >
            Settings
          </MuiMenuItem> */}
          <Divider />
          <MuiMenuItem
            onClick={() => {
              handleCloseUserMenu();
              requestLogout();
            }}
          >
            <span className="notranslate" translate="no">
              {myTranslate("navbar.logout")}
            </span>
          </MuiMenuItem>
        </Menu>
      )}

      <LogoutConfirmDialog
        open={isLogoutDialogOpen}
        onClose={closeLogoutDialog}
        onConfirm={confirmLogout}
      />
    </>
  );
};

export default UserActions;
